import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema';

import type { ExperienceTrackerAPI } from '@confluence/experience-tracker';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { END } from '@confluence/navdex';

import { InlineCommentFramework } from './enum/InlineCommentFramework';
import { getTargetNodeType } from './analyticsUtils';

export const handleResolveSuccess = ({
	commentId,
	markerRef,
	pageId,
	pageMode,
	eventEmitter,
	source,
	removeUnresolvedInlineComment,
	createAnalyticsEvent,
	getInlineNodeTypes,
}: {
	commentId?: string;
	markerRef: string;
	pageId: string;
	pageMode: PageMode;
	eventEmitter: AnnotationUpdateEmitter;
	source: 'commentsPanel' | 'editPageScreen' | 'viewPageScreen';
	removeUnresolvedInlineComment: (unresolvedAnnotationId: string | null) => void;
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent;
	getInlineNodeTypes?: (annotationId: string) => string[] | undefined;
}) => {
	// Update the unresolved inline comments count
	removeUnresolvedInlineComment(markerRef);

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'resolved',
			actionSubject: 'comment',
			actionSubjectId: commentId,
			objectType: 'page',
			objectId: pageId,
			source,
			attributes: {
				commentType: 'inline',
				mode: pageMode,
				framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				navdexPointType: END,
				inlineNodeTypes:
					markerRef && getInlineNodeTypes ? getInlineNodeTypes(markerRef) : undefined,
				targetNodeType: getTargetNodeType(markerRef, pageMode !== PageMode.VIEW),
			},
		},
	}).fire();

	// Emit the event to mark the annotation as resolved
	if (pageMode === PageMode.VIEW) {
		eventEmitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
			[markerRef]: {
				id: markerRef,
				annotationType: AnnotationTypes.INLINE_COMMENT,
				state: AnnotationMarkStates.RESOLVED,
			},
		});
	} else {
		eventEmitter.emit('resolve', markerRef);
	}
};

export const handleMutationFailure = ({
	experienceTracker,
	experienceName,
	error,
}: {
	experienceTracker: ExperienceTrackerAPI;
	experienceName: string;
	error: Error;
}) => {
	experienceTracker.stopOnError({
		name: experienceName,
		error,
	});
};
